import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

/*
 * Banners institucionais do topo da home. Toda a cor vem das variáveis do portal (--portal-primary/--portal-secondary);
 * uma camada escura por cima garante texto branco legível em qualquer paleta, inclusive amarelo e laranja.
 */

const rise = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatIn = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const fill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

// Tom escuro derivado da cor do portal (fallback para navegadores sem color-mix)
const deepTone = css`
  color: #0f172a;
  color: color-mix(in srgb, var(--portal-secondary) 55%, #000);
`;

export const Stage = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  isolation: isolate;
  color: #fff;
  background-color: var(--portal-secondary);
  background-image: linear-gradient(105deg, rgba(6, 12, 24, 0.64) 0%, rgba(6, 12, 24, 0.52) 45%, rgba(6, 12, 24, 0.22) 100%);

  /* Brilho na cor primária atrás do painel */
  &::before {
    content: "";
    position: absolute;
    inset: -30% -15% -30% 40%;
    background: radial-gradient(closest-side, var(--portal-primary), transparent);
    opacity: 0.45;
    z-index: -1;
    pointer-events: none;
  }

  /* Grade de pontos à direita */
  &::after {
    content: "";
    position: absolute;
    inset: 0 0 0 48%;
    background-image: radial-gradient(rgba(255, 255, 255, 0.16) 1.2px, transparent 1.6px);
    background-size: 22px 22px;
    -webkit-mask-image: linear-gradient(to left, #000 10%, transparent 85%);
    mask-image: linear-gradient(to left, #000 10%, transparent 85%);
    z-index: -1;
    pointer-events: none;
  }

  @media (max-width: 900px) {
    background-image: linear-gradient(180deg, rgba(6, 12, 24, 0.58), rgba(6, 12, 24, 0.5));

    &::before {
      inset: 30% -40% -40% 10%;
      opacity: 0.3;
    }

    &::after {
      inset: 0 0 0 30%;
    }
  }
`;

// Anéis concêntricos decorativos
export const Rings = styled.svg`
  position: absolute;
  right: -180px;
  bottom: -260px;
  width: 720px;
  height: 720px;
  z-index: -1;
  pointer-events: none;
  color: rgba(255, 255, 255, 0.09);

  @media (max-width: 900px) {
    right: -320px;
    bottom: -380px;
  }
`;

// Empilha todos os slides na mesma célula: a altura acompanha o maior e a troca é um crossfade
export const Slides = styled.div`
  display: grid;

  > * {
    grid-area: 1 / 1;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  min-width: 0;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 16px 6px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.24);
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #fff;
    ${deepTone}
  }

  svg {
    font-size: 1.05rem;
  }
`;

export const Title = styled.h2`
  margin: 0;
  max-width: 20ch;
  font-size: clamp(1.875rem, 1.1rem + 2.4vw, 3.125rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.015em;
  text-wrap: balance;
  color: #fff;
`;

export const Description = styled.p`
  margin: 0;
  max-width: 54ch;
  font-size: 1.125rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
`;

const actionStyles = css<{ $variant: "primary" | "secondary" }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  svg {
    font-size: 1.15rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    text-decoration: none;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  ${({ $variant }) =>
    $variant === "primary"
      ? css`
          background: #fff;
          ${deepTone}
          &:hover {
            background: rgba(255, 255, 255, 0.9);
          }
        `
      : css`
          color: #fff;
          background: transparent;
          border: 1.5px solid rgba(255, 255, 255, 0.55);
          &:hover {
            background: rgba(255, 255, 255, 0.12);
            border-color: #fff;
          }
        `}
`;

export const ActionAnchor = styled.a<{ $variant: "primary" | "secondary" }>`
  ${actionStyles}
`;

export const ActionRouterLink = styled(Link)<{ $variant: "primary" | "secondary" }>`
  ${actionStyles}
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const Visual = styled.div`
  position: relative;
  justify-self: end;
  width: min(100%, 420px);
  margin-right: 12px;

  /* Cartões translúcidos atrás do painel, para dar profundidade */
  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 22px;
    border: 1px solid rgba(255, 255, 255, 0.18);
  }

  &::before {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(-6deg) translate(-18px, 6px);
  }

  &::after {
    background: rgba(255, 255, 255, 0.06);
    transform: rotate(4deg) translate(16px, 10px);
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

export const Panel = styled.div`
  position: relative;
  z-index: 1;
  padding: 22px;
  border-radius: 20px;
  background: #fff;
  color: #0f172a;
  box-shadow: 0 32px 64px -28px rgba(2, 6, 23, 0.6), 0 2px 6px rgba(2, 6, 23, 0.12);
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 18px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e8edf3;
`;

export const PanelIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  color: #fff;
  background-image: linear-gradient(rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0.16)),
    linear-gradient(135deg, var(--portal-primary), var(--portal-secondary));

  svg {
    font-size: 1.6rem;
  }
`;

export const PanelHeading = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-size: 1.0625rem;
    font-weight: 800;
    line-height: 1.3;
  }

  span {
    font-size: 0.875rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const PanelList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1e293b;
  }
`;

export const PanelItemIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #eef2f7;
  background: color-mix(in srgb, var(--portal-primary) 14%, #fff);
  ${deepTone}

  svg {
    font-size: 1.15rem;
  }
`;

export const LegalBadge = styled.span`
  position: absolute;
  z-index: 2;
  left: -28px;
  bottom: -18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: calc(100% + 28px);
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #fff;
  background: #0f172a;
  background: color-mix(in srgb, var(--portal-secondary) 45%, #050a14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 16px 32px -16px rgba(2, 6, 23, 0.7);

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
`;

// Definido depois de Content/Visual porque os referencia como seletores
export const Slide = styled.div<{ $active: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: center;
  gap: 56px;
  min-height: 460px;
  padding: 64px 160px 124px;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  transition: ${({ $active }) => ($active ? "opacity 0.6s ease, visibility 0s" : "opacity 0.6s ease, visibility 0s linear 0.6s")};

  ${({ $active }) =>
    $active &&
    css`
      ${Content} > * {
        animation: ${rise} 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
      }
      ${Content} > *:nth-child(2) { animation-delay: 0.07s; }
      ${Content} > *:nth-child(3) { animation-delay: 0.14s; }
      ${Content} > *:nth-child(4) { animation-delay: 0.21s; }
      ${Visual} {
        animation: ${floatIn} 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    ${Content} > *, ${Visual} {
      animation: none !important;
    }
  }

  @media (max-width: 1200px) {
    padding: 56px 80px 120px;
    gap: 40px;
  }

  @media (max-width: 960px) {
    padding: 48px 40px 116px;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
  }

  @media (max-width: 600px) {
    padding: 36px 20px 104px;
  }
`;

export const Controls = styled.div`
  position: absolute;
  left: 160px;
  right: 160px;
  bottom: 32px;
  display: flex;
  align-items: flex-end;
  gap: 24px;

  @media (max-width: 1200px) {
    left: 80px;
    right: 80px;
  }

  @media (max-width: 960px) {
    left: 40px;
    right: 40px;
  }

  @media (max-width: 600px) {
    left: 20px;
    right: 20px;
    bottom: 24px;
    gap: 12px;
  }
`;

export const Tabs = styled.div`
  flex: 1;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 14px;
  max-width: 760px;
  min-width: 0;
`;

export const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 10px 0 11px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  text-align: left;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255, 255, 255, 0.68)")};
  transition: color 0.2s ease;

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 4px;
    border-radius: 4px;
  }

  @media (max-width: 900px) {
    > span:first-child {
      display: none;
    }
  }
`;

export const TabTrack = styled.span<{ $active: boolean }>`
  display: block;
  height: 3px;
  border-radius: 3px;
  overflow: hidden;
  background: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.22)")};
`;

// Preenchimento do slide ativo: o fim da animação avança o carrossel, então pausar a animação pausa o carrossel.
// Parado pelo usuário, a barra fica cheia só para indicar o slide atual.
export const TabFill = styled.span<{ $duration: number; $paused: boolean; $stopped: boolean }>`
  display: block;
  height: 100%;
  background: #fff;
  transform-origin: left center;
  animation: ${({ $stopped, $duration }) => ($stopped ? "none" : css`${fill} ${$duration}ms linear forwards`)};
  animation-play-state: ${({ $paused }) => ($paused ? "paused" : "running")};
`;

// margin-left: auto encosta os botões à direita mesmo quando as abas atingem o max-width
export const NavButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

export const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  svg {
    font-size: 1.35rem;
  }
`;
